import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { API } from "../lib/api";
import { track } from "../lib/analytics";
import AppLayout from "../components/app/AppLayout";
import { CURRENCIES } from "../lib/currency";
import {
  ArrowLeft, Download, Clock, Euro, Cog, Sparkles,
  Boxes, Pencil, RefreshCw, Undo2, User, ShieldAlert, ShieldX,
  Eye, Target, Gauge, Cpu, Wrench, Phone, GitMerge,
} from "lucide-react";
import QuantityTiersTable from "../components/quote/QuantityTiersTable";
import EngineeringPanel from "../components/quote/EngineeringPanel";
import CalibrationDialog from "../components/quote/CalibrationDialog";
import EditAnalysisDialog from "../components/quote/EditAnalysisDialog";
import RevisionDiffDialog from "../components/quote/RevisionDiffDialog";
import ViewerTab from "../components/quote/ViewerTab";
import InsightsTab from "../components/quote/InsightsTab";
import OperationsTab from "../components/quote/OperationsTab";
import CostsTab from "../components/quote/CostsTab";
import { ScoreTab, MachineTab } from "../components/quote/MetaTabs";
import MachineCompareSection from "../components/quote/MachineCompareSection";
import OutsourceBanner from "../components/quote/OutsourceBanner";
import LinkedRfqsBadge from "../components/quote/LinkedRfqsBadge";
import GuardrailBadge from "../components/quote/GuardrailBadge";
import FeatureExtractionCard from "../components/quote/FeatureExtractionCard";
import DoneInOneBadge from "../components/quote/DoneInOneBadge";
import GcodeSkeletonCard from "../components/guardrail/GcodeSkeletonCard";
import { Metric, Tag } from "../components/quote/atoms";
import { PriceGateBanner, GatedPriceMetric, CostsLockedCard } from "../components/quote/PriceReliability";
import { priceGate } from "../lib/priceGate";
import { parseLot, parseMargin, quoteParamsError } from "../lib/quoteParams";

const fmtMin = (n) => `${(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} min`;

const TABS = [
  { key: "viewer",     labelKey: "quoteDetail.tabViewer",     icon: Eye },
  { key: "score",      labelKey: "quoteDetail.tabScore",      icon: Gauge },
  { key: "machine",    labelKey: "quoteDetail.tabMachine",    icon: Cpu },
  { key: "insights",   labelKey: "quoteDetail.tabInsights",   icon: Sparkles },
  { key: "operations", labelKey: "quoteDetail.tabOperations", icon: Cog },
  { key: "costs",      labelKey: "quoteDetail.tabCosts",      icon: Euro },
];

// Fetch a file behind /files/:path as a blob URL using cookie auth.
// Returns a cleanup tuple so the caller can revoke on unmount or dep change.
function useAuthedBlobUrl(path) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    let created = "";
    fetch(`${API}/files/${path}`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (cancelled || !blob) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [path]);
  return url;
}

export default function QuoteDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [marginPct, setMarginPct] = useState(25);
  const [batchSize, setBatchSize] = useState(1);
  const [currency, setCurrency] = useState("EUR");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [recalculating, setRecalculating] = useState(false);
  const [tab, setTab] = useState("viewer");
  const [zoom, setZoom] = useState(1);

  const [aiInsights, setAiInsights] = useState(null);
  const [aiInsightsLoading, setAiInsightsLoading] = useState(false);
  const [aiInsightsError, setAiInsightsError] = useState("");

  const [calibOpen, setCalibOpen] = useState(false);
  const [calibActuals, setCalibActuals] = useState({});
  const [calibSaving, setCalibSaving] = useState(false);
  const [calibError, setCalibError] = useState("");
  const [calibSuccess, setCalibSuccess] = useState(null);

  const [editAnalysisOpen, setEditAnalysisOpen] = useState(false);
  const [revisionDiffOpen, setRevisionDiffOpen] = useState(false);
  const [consultLoading, setConsultLoading] = useState(false);
  const [consultMsg, setConsultMsg] = useState("");

  const imageBlobUrl = useAuthedBlobUrl(q?.image_path);
  const pdfBlobUrl = useAuthedBlobUrl(q?.time_study_path);

  useEffect(() => {
    api.get(`/quotes/${id}`)
      .then((res) => {
        setQ(res.data);
        setMarginPct(res.data.margin_pct ?? 25);
        setBatchSize(res.data.batch_size ?? 1);
        setCurrency(res.data.currency || "EUR");
        setCustomerId(res.data.customer_id || "");
        if (res.data.ai_insights) setAiInsights(res.data.ai_insights);
      })
      .catch(() => setError(t("quoteDetail.notFound")));
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, [id]);

  const exportPDF = async (refreshFx = false) => {
    setDownloading(true);
    try {
      const qs = refreshFx ? "?refresh_fx=true" : "";
      const res = await fetch(`${API}/quotes/${id}/pdf${qs}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download fallito");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `preventivo-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      const totals = q?.calculation?.totals || {};
      const amount = typeof totals.total_eur === "number" ? totals.total_eur : null;
      const cur = totals.currency || q?.currency || "EUR";
      const fx = totals.fx_rate_from_eur || 1;
      // `totals.total_eur` holds the amount in the quote currency (legacy
      // name): total_eur is reported only once really converted to EUR.
      track("pdf_exported", {
        quote_id: id,
        refresh_fx: refreshFx,
        currency: cur,
        total_amount: amount,
        total_eur: amount == null ? null : Math.round((cur === "EUR" ? amount : amount / fx) * 100) / 100,
        amount_schema: 2,
      });
    } catch (_e) {
      alert(t("quoteDetail.pdfError"));
    }
    setDownloading(false);
  };

  const enterEditMode = () => {
    if (!q) return;
    const init = {};
    (q.calculation?.breakdown || []).forEach((b, i) => { init[i] = String(b.minutes_per_piece ?? 0); });
    setOverrides(init);
    setEditMode(true);
    setTab("operations");
  };

  const cancelEdit = () => {
    setEditMode(false);
    setOverrides({});
    setMarginPct(q.margin_pct ?? 25);
    setBatchSize(q.batch_size ?? 1);
    setCurrency(q.currency || "EUR");
    setCustomerId(q.customer_id || "");
  };

  // Applica/rimuove l'override macchina per una singola operazione e
  // ricalcola: la scelta manuale batte ogni regola automatica (incl. fantina).
  const applyMachineOverride = async (index, machineId) => {
    const { data } = await api.post(`/quotes/${id}/recalculate`, {
      overrides: [{ index, machine_id: machineId }],
    });
    setQ(data);
  };

  const recalcParamsError = quoteParamsError(batchSize, marginPct, t);
  const recalc = async () => {
    if (recalcParamsError) return;
    setRecalculating(true);
    try {
      const changed = [];
      (q.calculation?.breakdown || []).forEach((b, i) => {
        const v = parseFloat(overrides[i]);
        if (!isNaN(v) && Math.abs(v - (b.minutes_per_piece || 0)) > 0.001) {
          changed.push({ index: i, minutes_per_piece: v });
        }
      });
      const { data } = await api.post(`/quotes/${id}/recalculate`, {
        overrides: changed,
        margin_pct: parseMargin(marginPct),
        batch_size: parseLot(batchSize),
        currency,
        customer_id: customerId || null,
      });
      setQ(data);
      setEditMode(false);
      setOverrides({});
    } catch (e) {
      alert(`Errore ricalcolo: ${e.response?.data?.detail || e.message}`);
    }
    setRecalculating(false);
  };

  const generateInsights = async () => {
    setAiInsightsLoading(true);
    setAiInsightsError("");
    try {
      const { data } = await api.post(`/quotes/${id}/ai-insights`);
      setAiInsights(data.insights);
    } catch (e) {
      setAiInsightsError(e.response?.data?.detail || t("quoteDetail.insightsGenError"));
    }
    setAiInsightsLoading(false);
  };

  const openCalibration = () => {
    const init = {};
    (q.calculation?.breakdown || []).forEach((b, i) => { init[i] = String(b.minutes_per_piece ?? 0); });
    setCalibActuals(init);
    setCalibError("");
    setCalibSuccess(null);
    setCalibOpen(true);
  };

  const submitCalibration = async () => {
    setCalibSaving(true);
    setCalibError("");
    try {
      const actuals = [];
      (q.calculation?.breakdown || []).forEach((b, i) => {
        const v = parseFloat(calibActuals[i]);
        if (!isNaN(v) && v > 0) actuals.push({ index: i, actual_minutes: v });
      });
      if (actuals.length === 0) {
        setCalibError(t("quoteDetail.calibNeedOne"));
        setCalibSaving(false);
        return;
      }
      const { data } = await api.post(`/quotes/${id}/calibrate`, { actuals });
      setCalibSuccess(data.updates || []);
      setQ((prev) => prev ? { ...prev, calibrated: true } : prev);
    } catch (e) {
      setCalibError(e.response?.data?.detail || t("quoteDetail.calibGenError"));
    }
    setCalibSaving(false);
  };

  const saveAnalysis = async (payload) => {
    const { data } = await api.post(`/quotes/${id}/update-analysis`, payload);
    setQ(data);
  };

  const requestConsultation = async () => {
    if (consultLoading) return;
    setConsultLoading(true);
    setConsultMsg("");
    try {
      const { data } = await api.post(`/quotes/${id}/request-consultation`, {});
      setConsultMsg(
        data.email_status === "live"
          ? t("quoteDetail.consultSentUser")
          : t("quoteDetail.consultSentAdmin")
      );
    } catch (e) {
      setConsultMsg(e?.response?.data?.detail || t("quoteDetail.consultError"));
    } finally {
      setConsultLoading(false);
      setTimeout(() => setConsultMsg(""), 6000);
    }
  };

  const a = q?.analysis || {};
  const c = q?.calculation || {};
  const totals = c?.totals || {};
  const dims = a?.estimated_dimensions_mm || {};

  const totalAlerts = useMemo(() => {
    if (!aiInsights) return 0;
    return (aiInsights.production_risks || []).filter((r) => r.severity === "alert").length;
  }, [aiInsights]);

  if (error) return <AppLayout><div className="p-8 text-red-400">{error}</div></AppLayout>;
  if (!q) return <AppLayout><div className="p-8 text-slate-400">{t("common.loading")}</div></AppLayout>;

  return (
    <AppLayout>
      <div className="px-6 lg:px-8 py-6 max-w-[1600px] mx-auto" data-testid="quote-detail">
        <button onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/dashboard"))} className="text-slate-400 hover:text-slate-200 text-sm flex items-center gap-2 mb-4" data-testid="back-btn">
          <ArrowLeft size={14}/> {t("common.back")}
        </button>

        {/* HEADER */}
        <div className="flex items-start justify-between mb-5 gap-6 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="label-tech mb-2">{t("quoteDetail.quoteLabel")} · {q.id.slice(0, 8)}</div>
            <h1 className="font-display font-bold text-3xl text-slate-100 truncate">{a.part_description || t("quoteDetail.partAnalyzed")}</h1>
            <div className="mt-3 flex items-center gap-3 text-sm text-slate-400 flex-wrap">
              <span className="font-mono-data">{q.material_name}</span>
              <span>·</span>
              <span>{t("quoteDetail.batch")} {q.batch_size}</span>
              <span>·</span>
              <span className="font-mono-data text-blue-300">{q.currency || "EUR"}</span>
              {q.source === "time_study_pdf" && <Tag color="amber">{t("quoteDetail.fromTimeStudy")}</Tag>}
              {q.customer?.name && (
                <span className="flex items-center gap-1.5 text-blue-300 font-medium" data-testid="header-customer">
                  <User size={13}/> {q.customer.name}
                </span>
              )}
              <Tag color="blue">{t("quoteDetail.complexityTag", { v: a.complexity })}</Tag>
              <Tag color="amber">{t("quoteDetail.toleranceTag", { v: a.tolerance_class })}</Tag>
              <Tag color="emerald">Ra {a.surface_finish_ra_um} µm</Tag>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={openCalibration} disabled={editMode} className="px-4 py-3 rounded-md bg-slate-800 border border-slate-700 text-slate-200 hover:border-amber-500 hover:text-amber-300 flex items-center gap-2 disabled:opacity-50" data-testid="open-calibrate-btn" title={t("quoteDetail.calibrateTitle")}>
              <Target size={16}/> {q.calibrated ? t("quoteDetail.recalibrate") : t("quoteDetail.calibrate")}
            </button>
            {q.drawing_requirements && (
              <button
                onClick={() => setRevisionDiffOpen(true)}
                disabled={editMode}
                className="px-4 py-3 rounded-md bg-slate-800 border border-slate-700 text-slate-200 hover:border-blue-500 hover:text-blue-300 flex items-center gap-2 disabled:opacity-50"
                data-testid="open-revision-diff-btn"
                title={t("quoteDetail.compareRevisionHint", { defaultValue: "Carica una nuova revisione del disegno per evidenziare le differenze rispetto a questo preventivo" })}
              >
                <GitMerge size={16}/> {t("quoteDetail.compareRevision", { defaultValue: "Confronta revisione" })}
              </button>
            )}
            <button onClick={() => exportPDF(false)} disabled={downloading || editMode} className="btn-primary px-5 py-3 rounded-md flex items-center gap-2 disabled:opacity-50" data-testid="export-pdf-btn">
              <Download size={16}/> {downloading ? t("quoteDetail.generating") : t("common.exportPdf")}
            </button>
            {q.currency && q.currency !== "EUR" && (
              <button onClick={() => exportPDF(true)} disabled={downloading || editMode} className="px-4 py-3 rounded-md bg-slate-800 border border-slate-700 text-slate-200 hover:border-emerald-500 hover:text-emerald-300 flex items-center gap-2 disabled:opacity-50 text-sm" data-testid="export-pdf-live-fx-btn" title={t("quoteDetail.liveFxTitle")}>
                <Download size={14}/> PDF · Live FX
              </button>
            )}
          </div>
        </div>

        {/* TOP METRICS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <GatedPriceMetric guardrail={q.guardrail} total={totals.total_eur} currency={q.currency} testid="metric-total" icon={<Euro size={16}/>} label={t("quoteDetail.metricTotal")} />
          <GatedPriceMetric guardrail={q.guardrail} total={totals.unit_price_eur} currency={q.currency} testid="metric-unit-price" icon={<Boxes size={16}/>} label={t("quoteDetail.metricUnit")} />
          <Metric testid="metric-cycle-time" icon={<Clock size={16}/>} label={t("quoteDetail.metricCycle")}  value={fmtMin(totals.cycle_time_minutes_per_piece)} />
          <Metric testid="metric-setup" icon={<Cog size={16}/>}   label={t("quoteDetail.doneInOneSetupLabel")}      value={`${(totals.total_setup_minutes || 0).toFixed(0)} min`} />
        </div>

        <PriceGateBanner guardrail={q.guardrail} total={totals.total_eur} currency={q.currency} />

        {/* AI ESTIMATION DISCLAIMER */}
        <div
          className="mb-6 border border-amber-500/40 bg-amber-500/[0.06] rounded-sm px-4 py-3 flex items-start gap-3"
          data-testid="ai-estimation-disclaimer"
        >
          <div className="w-7 h-7 rounded-sm bg-amber-500/15 border border-amber-500/40 flex items-center justify-center shrink-0 mt-0.5">
            <ShieldAlert size={14} className="text-amber-300"/>
          </div>
          <div className="flex-1">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-amber-300 mb-1">// AI_ESTIMATION_NOTICE</div>
            <p className="text-sm text-slate-300 leading-relaxed">
              AI-generated estimation for manufacturing planning purposes.
              <span className="text-slate-400"> Final quotation must be reviewed and approved by the machining operator.</span>
            </p>
          </div>
        </div>

        <EngineeringPanel quoteId={q.id}/>

        <GuardrailBadge guardrail={q.guardrail}/>

        <FeatureExtractionCard quote={q} onQuoteUpdate={setQ}/>

        <DoneInOneBadge quote={q}/>

        <OutsourceBanner quote={q}/>

        <LinkedRfqsBadge quoteId={q.id}/>

        {/* TABS */}
        <div className="border-b border-slate-800 mb-5 flex items-center gap-1 overflow-x-auto" data-testid="quote-tabs">
          {TABS.map((tb) => {
            const Icon = tb.icon;
            const active = tab === tb.key;
            const badge = tb.key === "insights" && totalAlerts > 0 ? totalAlerts : null;
            const locked = tb.key === "costs" && priceGate(q.guardrail).blocked;
            return (
              <button
                key={tb.key}
                onClick={() => !locked && setTab(tb.key)}
                disabled={locked}
                title={locked ? t("priceGate.costsLockedBody") : ""}
                className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
                  locked ? "border-transparent text-slate-600 cursor-not-allowed" :
                  active ? "border-blue-500 text-blue-300" : "border-transparent text-slate-400 hover:text-slate-200"
                }`}
                data-testid={`tab-${tb.key}`}
              >
                <Icon size={15}/> {t(tb.labelKey, { defaultValue: "AI Insights" })}
                {locked && <ShieldX size={12} className="text-rose-400"/>}
                {badge !== null && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-300 text-[10px] font-mono-data border border-red-500/40">{badge}</span>
                )}
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-2 py-2 pl-4">
            {!editMode && (
              <button
                onClick={() => setEditAnalysisOpen(true)}
                className="px-3 py-1.5 rounded-md text-xs bg-slate-800 text-slate-200 border border-slate-700 hover:border-amber-500 hover:text-amber-300 flex items-center gap-2"
                data-testid="edit-analysis-btn"
                title={t("quoteDetail.editAnalysisHint")}
              >
                <Wrench size={12}/> {t("editAnalysis.title")}
              </button>
            )}
            {!editMode ? (
              <button
                onClick={enterEditMode}
                className="px-3 py-1.5 rounded-md text-xs bg-slate-800 text-slate-200 border border-slate-700 hover:border-blue-500 flex items-center gap-2"
                data-testid="edit-quote-btn"
              >
                <Pencil size={12}/> {t("common.edit")}
              </button>
            ) : (
              <>
                <button onClick={recalc} disabled={recalculating || !!recalcParamsError} className="btn-primary px-3 py-1.5 rounded-md text-xs flex items-center gap-2 disabled:opacity-50" data-testid="recalc-btn">
                  <RefreshCw size={12} className={recalculating ? "animate-spin" : ""}/> {recalculating ? "..." : t("quoteDetail.recalc")}
                </button>
                <button onClick={cancelEdit} className="px-3 py-1.5 rounded-md text-xs bg-slate-800 text-slate-300 border border-slate-700 hover:border-slate-500 flex items-center gap-2" data-testid="cancel-edit-btn">
                  <Undo2 size={12}/> {t("common.cancel")}
                </button>
              </>
            )}
          </div>
        </div>

        {editMode && (
          <div className="mb-5 p-3 rounded-md bg-slate-900/60 border border-slate-800 flex items-center gap-2 flex-wrap text-xs">
            <span className="label-tech text-[10px]">{t("quoteDetail.batch")}</span>
            <input type="number" min="1" value={batchSize} onChange={(e) => setBatchSize(e.target.value)} className="w-20 px-2 py-1 rounded text-sm font-mono-data" data-testid="edit-batch-size"/>
            <span className="label-tech text-[10px] ml-2">{t("quoteDetail.editMargin")}</span>
            <input type="number" min="0" max="200" value={marginPct} onChange={(e) => setMarginPct(e.target.value)} className="w-20 px-2 py-1 rounded text-sm font-mono-data" data-testid="edit-margin"/>
            {recalcParamsError && <span className="text-xs text-red-400 ml-2" data-testid="recalc-params-error">{recalcParamsError}</span>}
            <span className="label-tech text-[10px] ml-2">{t("quoteDetail.editCurrency")}</span>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="px-2 py-1 rounded text-sm font-mono-data" data-testid="edit-currency">
              {CURRENCIES.map((cc) => <option key={cc.code} value={cc.code}>{cc.code}</option>)}
            </select>
            <span className="label-tech text-[10px] ml-2">{t("quoteDetail.editCustomer")}</span>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="px-2 py-1 rounded text-sm max-w-[200px]" data-testid="edit-customer">
              <option value="">{t("quoteDetail.noneOption")}</option>
              {customers.map((cu) => <option key={cu.id} value={cu.id}>{cu.name}</option>)}
            </select>
          </div>
        )}

        {tab === "viewer"     && <ViewerTab q={q} a={a} c={c} dims={dims} imageBlobUrl={imageBlobUrl} pdfBlobUrl={pdfBlobUrl} zoom={zoom} setZoom={setZoom} onQuoteUpdate={setQ}/>}
        {tab === "score"      && <ScoreTab a={a}/>}
        {tab === "machine"    && (
          <div className="space-y-3">
            <MachineTab a={a}/>
            {a?.suggested_machine?.category && (
              <div className="tech-card p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="machine-consult-card">
                <div className="text-sm text-slate-300">
                  <div className="font-display font-medium text-slate-100 mb-1">{t("quoteDetail.consultTitle")}</div>
                  <div className="text-xs text-slate-400">{t("quoteDetail.consultBody")}</div>
                </div>
                <div className="flex items-center gap-2">
                  {consultMsg && (
                    <span className="text-[11px] text-emerald-300 font-mono-data" data-testid="machine-consult-msg">{consultMsg}</span>
                  )}
                  <button
                    onClick={requestConsultation}
                    disabled={consultLoading}
                    className="btn-primary px-4 py-2 rounded text-sm flex items-center gap-2 disabled:opacity-60 whitespace-nowrap"
                    data-testid="request-consultation-btn"
                  >
                    <Phone size={14}/> {consultLoading ? t("quoteDetail.sending") : t("quoteDetail.requestConsult")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {tab === "insights"   && <InsightsTab insights={aiInsights} loading={aiInsightsLoading} error={aiInsightsError} onGenerate={generateInsights} warnings={a.warnings} productionRisks={a.production_risks}/>}
        {tab === "operations" && (
          <div className="space-y-5">
            <OperationsTab q={q} c={c} editMode={editMode} overrides={overrides} setOverrides={setOverrides} onApplyMachine={applyMachineOverride}/>
            <GcodeSkeletonCard
              quoteId={q.id}
              defaultMaterial={q.material_name}
              defaultDimensions={dims}
            />
          </div>
        )}
        {tab === "costs"      && (
          priceGate(q.guardrail).blocked ? (
            <CostsLockedCard guardrail={q.guardrail}/>
          ) : (
            <div className="space-y-4">
              <PriceGateBanner guardrail={q.guardrail} total={totals.total_eur} currency={q.currency}/>
              <CostsTab q={q} c={c} t={totals} a={a}/>
              <QuantityTiersTable tiers={c.quantity_tiers || []} currency={totals.currency}/>
              <MachineCompareSection quoteId={q.id} currencySymbol={CURRENCIES[q.currency || "EUR"]?.symbol || "€"} />
            </div>
          )
        )}

        {calibOpen && (
          <CalibrationDialog
            breakdown={c.breakdown || []}
            actuals={calibActuals}
            setActuals={setCalibActuals}
            saving={calibSaving}
            error={calibError}
            success={calibSuccess}
            onClose={() => setCalibOpen(false)}
            onSubmit={submitCalibration}
          />
        )}

        {editAnalysisOpen && (
          <EditAnalysisDialog
            analysis={{
              ...a,
              _material_price_override_eur_per_kg: q.material_price_override_eur_per_kg ?? null,
              _material_price_catalog_eur_per_kg: c?.material?.catalog_price_per_kg_eur ?? null,
              _raw_weight_kg_override: q.raw_weight_kg_override ?? null,
              _finished_weight_kg: q.finished_weight_kg ?? null,
              _computed_raw_weight_kg: c?.material?.weight_kg ?? null,
            }}
            onClose={() => setEditAnalysisOpen(false)}
            onSave={saveAnalysis}
          />
        )}

        <RevisionDiffDialog
          open={revisionDiffOpen}
          onOpenChange={setRevisionDiffOpen}
          quoteId={q.id}
        />
      </div>
    </AppLayout>
  );
}
